import { ORDERED_SUPPORTED_LANGUAGES } from '@amzn/atoz-supported-languages';
import { DEFAULT_LOCALE } from '../constants';

export const LOCALE_STORAGE_KEY = 'myhr-lite-language-preferred';

export const RTL_LOCALES = ['ar-SA', 'fa-AF', 'ur-PK'];

export const LOCALE_FALLBACKS: Record<string, string> = {
    'en-AU': 'en-GB',
    'fr-BE': 'fr-FR',
    'hu-HU': 'en-US',
    'kn-IN': 'en-IN',
    'lt-LT': 'en-US',
    'mr-IN': 'hi-IN',
    'ms-MY': 'id-ID',
    'my-MM': 'en-US',
    'sv-SE': 'en-US',
    'th-TH': 'en-US',
    'nl-BE': 'nl-NL',
    'cs-CZ': 'cs',
    'sk-SK': 'sk',
};

const supportedLocales = (ORDERED_SUPPORTED_LANGUAGES || []).map((lang) => lang.locale);

export function isRTLLocale(locale: string): boolean {
    return RTL_LOCALES.includes(locale);
}

export function applyDocumentDirection(locale: string): void {
    document.documentElement.dir = isRTLLocale(locale) ? 'rtl' : 'ltr';
    document.documentElement.lang = locale;
}

function findSupportedLocale(localeCode: string): string | undefined {
    if (supportedLocales.includes(localeCode)) {
        return localeCode;
    }

    if (LOCALE_FALLBACKS[localeCode] && supportedLocales.includes(LOCALE_FALLBACKS[localeCode])) {
        return LOCALE_FALLBACKS[localeCode];
    }

    const languageOnly = localeCode.split('-')[0];
    return supportedLocales.find((locale) => locale.startsWith(languageOnly + '-'));
}

export function detectBrowserLocale(): string {
    const browserLocale = navigator.language;
    const matchedLocale = findSupportedLocale(browserLocale);
    return matchedLocale || DEFAULT_LOCALE;
}

export function detectLocale(): string {
    let storedLocale: string | null = null;
    try {
        storedLocale = localStorage.getItem(LOCALE_STORAGE_KEY);
    } catch {
        // localStorage unavailable (private browsing mode)
    }

    const browserLocale = navigator.language;
    const matchedLocale = findSupportedLocale(browserLocale);

    if (storedLocale && supportedLocales.includes(storedLocale)) {
        return storedLocale;
    }

    if (matchedLocale) {
        return matchedLocale;
    }

    return DEFAULT_LOCALE;
}

export function persistLocale(locale: string): void {
    try {
        localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    } catch {
        // localStorage unavailable (private browsing mode)
    }
}

export function clearLocale(): void {
    try {
        localStorage.removeItem(LOCALE_STORAGE_KEY);
    } catch {
        // localStorage unavailable (private browsing mode)
    }
}
